import React, { useState } from 'react';
import { PageWithStatus, PageId } from '../types';
import { CompletedStatusIcon, ActiveStatusIcon, UpcomingStatusIcon } from './Icons';
import { getRcUserRole, canToggleClientView, hasStaffPrivileges } from '../user_constant';

interface SidebarProps {
  pages: PageWithStatus[];
  onPageChange: (id: PageId) => void;
  isClientView: boolean;
  onClientViewChange: (isClient: boolean) => void;
  totalCompleteness: number;
  onDownloadReport: () => void;
}

const NavLink: React.FC<{ page: PageWithStatus; onClick: (id: PageId) => void; isLast: boolean; }> = ({ page, onClick, isLast }) => {
  const { status, level, IconComponent, title, id, completeness } = page;
  const formPages: PageId[] = ['admission', 'schedule1', 'schedule2', 'schedule3', 'acceptance', 'pdpa'];
  const showCompleteness = formPages.includes(id);

  // All pages are now enabled for navigation.
  const isDisabled = false;
  
  const statusConfig = {
    completed: {
      classes: 'text-slate-700 hover:bg-slate-100 hover:text-slate-900',
      text: 'text-slate-700 group-hover:text-slate-900',
      iconColor: 'text-slate-600 group-hover:text-slate-900'
    },
    active: {
      classes: 'bg-brand-primary-light text-brand-primary font-semibold',
      text: 'text-brand-primary',
      iconColor: 'text-brand-primary'
    },
    upcoming: {
      // Updated to allow interaction
      classes: 'text-slate-500 hover:bg-slate-100 hover:text-slate-800',
      text: 'text-slate-500 group-hover:text-slate-800',
      iconColor: 'text-slate-500 group-hover:text-slate-800'
    }
  };
  
  const getStatusIcon = () => {
    // FIX: The check `if (id === 'download')` was removed as it's unreachable.
    // The 'download' case is handled by an early return at the component's top level,
    // which narrows the type of `id` and causes a type error on this redundant check.
    switch (status) {
      case 'completed': return CompletedStatusIcon;
      case 'active': return ActiveStatusIcon;
      default: return UpcomingStatusIcon;
    }
  };

  const StatusIcon = getStatusIcon();
  const currentStatus = statusConfig[status];

  return (
    <li className="relative">
      {!isLast && level === 1 && id !== 'welcome' && (
        <div className="absolute left-[21px] top-8 h-full w-0.5 bg-slate-200" aria-hidden="true"></div>
      )}
      <button
        onClick={() => onClick(id)}
        disabled={isDisabled}
        className={`group flex items-center w-full text-left p-3 my-0.5 rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-primary/50 ${currentStatus.classes}`}
        aria-current={status === 'active' ? 'page' : undefined}
      >
        {level === 1 && (
          <div className="relative mr-4 flex h-7 w-7 items-center justify-center">
            {StatusIcon && <StatusIcon className="h-5 w-5 z-10" />}
          </div>
        )}
        
        <div className={`flex items-center w-full ${level === 2 ? 'pl-12' : ''}`}>
           {IconComponent && level === 1 && <IconComponent className={`flex-shrink-0 w-5 h-5 mr-3 ${currentStatus.iconColor} transition-colors`} />}
          <span className={`font-medium text-sm ${currentStatus.text} transition-colors`}>{title}</span>
           {showCompleteness && (
              <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${completeness === 100 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                  {completeness}%
              </span>
          )}
        </div>
      </button>
    </li>
  );
};


const Sidebar: React.FC<SidebarProps> = ({ pages, onPageChange, isClientView, onClientViewChange, totalCompleteness, onDownloadReport }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleLinkClick = (id: PageId) => {
    if (id === 'download') {
      onDownloadReport();
    } else {
      onPageChange(id);
    }
    setIsOpen(false); // Close menu on mobile after selection
  };
  
  const navPages = pages.filter(p => p.id !== 'download');
  const downloadPage = pages.find(p => p.id === 'download');

  return (
    <aside className="w-full md:w-72 bg-white md:border-r border-slate-200 flex-shrink-0 z-20 md:shadow-none shadow-md print:hidden">
      <div className="p-4 flex flex-col md:h-full">
        <div className="flex items-center justify-between mb-0 md:mb-6 md:px-2">
          {/* Logo container */}
          <div className="flex items-center" style={{position: "fixed", top: "14px"}}>
            <img src="https://mycareconcierge.com/wp-content/uploads/2024/10/care_concierge_logo_300x300.png" alt="Care Concierge Logo" className="h-10 sm:h-12 w-auto" />
          </div>
          {/* Hamburger button */}
          <button
            className="md:hidden p-1 rounded-md text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
            onClick={() => setIsOpen(!isOpen)}
            aria-controls="mobile-menu"
            aria-expanded={isOpen}
            aria-label="Toggle menu"
          >
            <span className="sr-only">Open main menu</span>
            {isOpen ? (
              <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
        <nav className={`flex-grow mt-4 md:mt-0 md:block ${isOpen ? 'block' : 'hidden'}`} id="mobile-menu" style={{position: "fixed", top: "60px"}}>
          <ul>
            {navPages.map((page, index) => (
              <NavLink 
                key={page.id}
                page={page}
                onClick={handleLinkClick}
                isLast={index === navPages.length - 1}
              />
            ))}
          </ul>
        </nav>

        <div className={`mt-auto pt-6 border-t border-slate-200 md:block ${isOpen ? 'block' : 'hidden'}` } style={{position: "fixed", bottom: "10px", width: "15rem"}}>
            <div className="px-2 mb-6">
                <h4 className="text-sm font-semibold text-slate-800 mb-2">Total Progress</h4>
                <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-slate-500">Completed</span>
                    <span className="text-xs font-bold text-brand-primary">{totalCompleteness}%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2.5">
                    <div 
                        className="bg-brand-primary h-2.5 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${totalCompleteness}%` }}
                    ></div>
                </div>
            </div>

            {downloadPage && (
              <div className="px-2 mb-6">
                <button
                  onClick={() => handleLinkClick(downloadPage.id)}
                  className="group flex items-center justify-center w-full text-center py-2.5 px-3 rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary bg-brand-primary text-white font-semibold shadow-md hover:bg-brand-primary/90"
                  aria-label={downloadPage.title}
                >
                  {downloadPage.IconComponent && <downloadPage.IconComponent className="flex-shrink-0 w-5 h-5 mr-2" />}
                  <span className="font-medium text-sm">{downloadPage.title}</span>
                </button>
              </div>
            )}


            <div className="flex items-center justify-between px-2">
                <label htmlFor="client-view-toggle" className="text-sm font-semibold text-slate-700">
                    Client View
                </label>
                <button
                    id="client-view-toggle"
                    type="button"
                    disabled={!canToggleClientView()}
                    className={`${
                    isClientView ? 'bg-brand-primary' : 'bg-slate-300'
                    } ${!canToggleClientView() ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2`}
                    role="switch"
                    aria-checked={isClientView}
                    onClick={() => canToggleClientView() && onClientViewChange(!isClientView)}
                >
                    <span className="sr-only">Toggle Client View</span>
                    <span
                    aria-hidden="true"
                    className={`${
                        isClientView ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                    />
                </button>
            </div>
            
            {/* Role indicator for staff members */}
            {hasStaffPrivileges() && (
                <div className="px-2 mt-2">
                    <div className="text-xs text-slate-500 text-center">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Staff Access
                        </span>
                    </div>
                </div>
            )}
        </div>

      </div>
    </aside>
  );
};

export default Sidebar;